import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, LogOut, FileText, BadgeCheck, ShieldCheck, DollarSign, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGE_STYLES = {
  Submitted: 'bg-slate-100 text-slate-700 border-slate-200',
  Quoted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Bound: 'bg-green-100 text-green-800 border-green-300',
  Declined: 'bg-rose-50 text-rose-700 border-rose-200',
  Closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const fmtMoney = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const MetricCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4 shadow-sm">
    <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-2xl font-black text-slate-900 leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{value}</div>
      <div className="text-sm text-slate-500 mt-0.5">{label}</div>
    </div>
  </div>
);

const PartnerPortal = () => {
  const { slug } = useParams();
  const tokenKey = `partner_token_${slug}`;
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem(`partner_token_${slug}`));
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/partner/${slug}/info`)
      .then((res) => setInfo(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const loadSummary = useCallback(async (tkn) => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API_URL}/partner/${slug}/summary`, { headers: { Authorization: `Bearer ${tkn}` } });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(tokenKey);
        setToken(null);
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoadingData(false);
    }
  }, [slug, tokenKey]);

  useEffect(() => {
    if (token) loadSummary(token);
  }, [token, loadSummary]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API_URL}/partner/${slug}/login`, { password });
      sessionStorage.setItem(tokenKey, res.data.token);
      setToken(res.data.token);
      setPassword('');
    } catch (err) {
      toast.error(err.response?.status === 401 ? 'Incorrect password' : 'Unable to sign in');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(tokenKey);
    setToken(null);
    setData(null);
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="partner-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Portal Not Available</h1>
          <p className="text-slate-500">This partner portal doesn't exist or hasn't been activated.</p>
        </div>
      </div>
    );
  }

  // ===== LOGIN SCREEN =====
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="partner-login-screen">
        <Helmet><title>{info ? `${info.name} — Partner Portal` : 'Partner Portal'}</title></Helmet>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="h-12 w-12 rounded-xl bg-[#EEF0FF] flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-[#7B6FE8]" />
              </div>
              <h1 className="text-2xl font-black text-slate-900 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {info ? info.name : 'Partner'} Portal
              </h1>
              <p className="text-sm text-slate-500 mt-1">Enter your portal password to view referral activity.</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                data-testid="partner-password-input"
                placeholder="Portal password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-slate-300 focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
              />
              <Button
                type="submit"
                data-testid="partner-login-btn"
                disabled={loggingIn}
                className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-semibold"
              >
                {loggingIn ? 'Signing in…' : 'View My Dashboard'}
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">Powered by Players Health</p>
        </div>
      </div>
    );
  }

  // ===== DASHBOARD =====
  const metrics = data?.metrics || {};
  const orgs = data?.organizations || [];
  const aff = data?.affiliate || info || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-dashboard">
      <Helmet><title>{aff.name ? `${aff.name} — Partner Portal` : 'Partner Portal'}</title></Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {aff.logo_url ? (
              <img src={aff.logo_url} alt={aff.name} className="h-9 w-auto object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-lg bg-[#EEF0FF] flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[#7B6FE8]" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{aff.name} Partner Portal</h1>
              <p className="text-xs text-slate-400 mt-0.5">Referral activity · Powered by Players Health</p>
            </div>
          </div>
          <Button onClick={handleLogout} data-testid="partner-logout-btn" variant="outline" size="sm" className="border-slate-300 text-slate-600 hover:bg-slate-100">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Metrics */}
        <div className={`grid gap-4 mb-8 ${metrics.estimated_earnings != null ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
          <MetricCard testid="metric-total" icon={FileText} label="Total Applications" value={metrics.total ?? 0} accent="bg-slate-100 text-slate-600" />
          <MetricCard testid="metric-quoted" icon={BadgeCheck} label="Reached Quote" value={metrics.reached_quote ?? 0} accent="bg-emerald-50 text-emerald-600" />
          <MetricCard testid="metric-bound" icon={ShieldCheck} label="Bound" value={metrics.bound ?? 0} accent="bg-green-100 text-green-700" />
          {metrics.estimated_earnings != null && (
            <MetricCard testid="metric-earnings" icon={DollarSign} label="Estimated Earnings" value={fmtMoney(metrics.estimated_earnings)} accent="bg-[#EEF0FF] text-[#7B6FE8]" />
          )}
        </div>

        {/* Org table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-base font-semibold text-slate-900">Organizations You Referred</h2>
            <p className="text-sm text-slate-500 mt-0.5">Every org that started an application from your page, and how far they got.</p>
          </div>
          {loadingData ? (
            <div className="py-16 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7B6FE8]"></div>
            </div>
          ) : orgs.length === 0 ? (
            <p className="text-center text-slate-500 py-16" data-testid="partner-empty-state">No applications yet. They'll appear here as soon as an organization applies through your page.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="partner-org-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="px-6 py-3 font-semibold">Organization</th>
                    <th className="px-6 py-3 font-semibold">Date Applied</th>
                    <th className="px-6 py-3 font-semibold">Stage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orgs.map((o, i) => (
                    <tr key={i} data-testid={`partner-org-row-${i}`} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">{o.org_name}</td>
                      <td className="px-6 py-4 text-slate-500">{fmtDate(o.submitted_at)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full border ${STAGE_STYLES[o.stage_label] || STAGE_STYLES.Submitted}`}>
                          {o.stage_label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-6">Commission is earned when a referred organization reaches a quote. Figures are indicative and subject to final reconciliation by Players Health.</p>
      </main>
    </div>
  );
};

export default PartnerPortal;
